/*
 * Tutor Desk SaaS staging Settings Reference v2.
 *
 * This presentation layer is intentionally qualified by all three runtime
 * authorities: the TToria Final UI standard, the SaaS staging target, and the
 * explicit Settings profile. Production, standalone, and the gated platform
 * administration panels continue to use their existing presentation.
 */
export const TUTOR_DESK_SETTINGS_REFERENCE_V2_STYLES = `
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] {
  --tds-v2-canvas: #f5f8fc;
  --tds-v2-surface: #ffffff;
  --tds-v2-surface-soft: #f8faff;
  --tds-v2-surface-hover: #edf4ff;
  --tds-v2-border: #dce5f1;
  --tds-v2-border-strong: #bdcadc;
  --tds-v2-text: #0b1930;
  --tds-v2-muted: #5d6f8c;
  --tds-v2-action: #0b6df8;
  --tds-v2-action-hover: #075dcc;
  --tds-v2-action-soft: #e9f2ff;
  --tds-v2-action-contrast: #ffffff;
  --tds-v2-success: #138a55;
  --tds-v2-warning: #b66b06;
  --tds-v2-danger: #d4384e;
  --tds-v2-focus: 0 0 0 3px rgba(11, 109, 248, 0.24);
  --tds-v2-shadow: 0 1px 2px rgba(21, 39, 73, 0.04), 0 8px 28px rgba(21, 39, 73, 0.035);
  color-scheme: light;
  color: var(--tds-v2-text);
  background: var(--tds-v2-canvas);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"][data-kg-tutor-desk-theme-mode="dark"] .kg-settings-root[data-kg-settings-reference-v2="true"] {
  --tds-v2-canvas: #07111f;
  --tds-v2-surface: #0e1a2c;
  --tds-v2-surface-soft: #111f33;
  --tds-v2-surface-hover: #162943;
  --tds-v2-border: #233752;
  --tds-v2-border-strong: #385170;
  --tds-v2-text: #f3f7fc;
  --tds-v2-muted: #9fb0c8;
  --tds-v2-action: #1686ff;
  --tds-v2-action-hover: #4aa2ff;
  --tds-v2-action-soft: #102d4e;
  --tds-v2-action-contrast: #ffffff;
  --tds-v2-success: #4dcc91;
  --tds-v2-warning: #f2b458;
  --tds-v2-danger: #ff7182;
  --tds-v2-focus: 0 0 0 3px rgba(22, 134, 255, 0.36);
  --tds-v2-shadow: 0 1px 2px rgba(0, 0, 0, 0.18), 0 12px 32px rgba(0, 0, 0, 0.12);
  color-scheme: dark;
}

/* Page shell and the fixed Settings navigation column. */
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] > .ds-panel__body,
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-shell-body {
  gap: 0;
  padding: 0;
  overflow: visible;
  background: var(--tds-v2-canvas);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .ds-plugin-page-panel__content-region {
  min-height: 0;
  gap: 0;
  padding: 0;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  background: var(--tds-v2-canvas);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .ds-plugin-page-panel__header,
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .ds-plugin-page-panel__status-strip:empty,
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-final-app-identity--visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  border: 0;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  white-space: nowrap;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-layout {
  width: 100%;
  min-width: 0;
  min-height: calc(100dvh - 72px);
  display: grid;
  grid-template-columns: 312px minmax(0, 1fr);
  align-items: stretch;
  background: var(--tds-v2-canvas);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-nav {
  position: sticky;
  top: 72px;
  align-self: start;
  width: 312px;
  height: calc(100dvh - 72px);
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-right: 1px solid var(--tds-v2-border);
  background: var(--tds-v2-surface);
  color: var(--tds-v2-text);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-nav__header {
  display: none;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-nav__header h2 {
  margin: 0;
  color: var(--tds-v2-text);
  font-size: 25px;
  font-weight: 760;
  letter-spacing: -0.025em;
  line-height: 1.2;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-nav__header p {
  margin: 0;
  color: var(--tds-v2-muted);
  font-size: 13px;
  line-height: 1.45;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-nav__scroll {
  min-height: 0;
  display: grid;
  align-content: start;
  gap: 22px;
  padding: 38px 16px 28px;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-gutter: stable;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-nav__group {
  display: grid;
  gap: 7px;
  padding-bottom: 18px;
  border-bottom: 1px solid var(--tds-v2-border);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-nav__group:last-child {
  padding-bottom: 0;
  border-bottom: 0;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-nav__group > h3 {
  margin: 0;
  padding: 0 12px;
  color: var(--tds-v2-muted);
  font-size: 11px;
  font-weight: 750;
  letter-spacing: 0.1em;
  line-height: 1.4;
  text-transform: uppercase;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-nav__items {
  display: grid;
  gap: 4px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-nav__item {
  width: 100%;
  min-height: 46px;
  display: grid;
  grid-template-columns: 24px minmax(0, 1fr);
  align-items: center;
  gap: 12px;
  padding: 9px 12px;
  border: 1px solid transparent;
  border-radius: 11px;
  background: transparent;
  color: var(--tds-v2-muted);
  font: inherit;
  font-size: 14px;
  font-weight: 650;
  line-height: 1.35;
  text-align: left;
  cursor: pointer;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-nav__item:hover {
  border-color: var(--tds-v2-border);
  background: var(--tds-v2-surface-hover);
  color: var(--tds-v2-text);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-nav__item.is-active {
  border-color: color-mix(in srgb, var(--tds-v2-action) 18%, var(--tds-v2-border));
  background: var(--tds-v2-action-soft);
  color: var(--tds-v2-action);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-nav__item:focus-visible,
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-nav-toggle:focus-visible,
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-nav__close:focus-visible {
  outline: 2px solid var(--tds-v2-action);
  outline-offset: 2px;
  box-shadow: var(--tds-v2-focus);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-nav__icon {
  width: 22px;
  height: 22px;
  display: block;
  background: currentColor;
  mask-position: center;
  mask-repeat: no-repeat;
  mask-size: contain;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] [data-settings-reference-v2-icon="appearance"] .kg-settings-reference-v2-nav__icon {
  mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M12 3a9 9 0 1 0 0 18h1.2a2 2 0 0 0 1.8-2.9l-.2-.5a2 2 0 0 1 1.8-2.9H19a3 3 0 0 0 3-3C22 6.8 17.5 3 12 3Z'/%3E%3Ccircle cx='7.5' cy='11' r='.7' fill='black' stroke='none'/%3E%3Ccircle cx='10' cy='7.5' r='.7' fill='black' stroke='none'/%3E%3Ccircle cx='14' cy='7' r='.7' fill='black' stroke='none'/%3E%3Ccircle cx='17' cy='10' r='.7' fill='black' stroke='none'/%3E%3C/svg%3E");
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] [data-settings-reference-v2-icon="profile"] .kg-settings-reference-v2-nav__icon {
  mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2'%3E%3Ccircle cx='12' cy='8' r='4'/%3E%3Cpath d='M4 22a8 8 0 0 1 16 0'/%3E%3C/svg%3E");
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] [data-settings-reference-v2-icon="schedule"] .kg-settings-reference-v2-nav__icon {
  mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2'%3E%3Crect x='3' y='5' width='18' height='16' rx='2'/%3E%3Cpath d='M16 3v4M8 3v4M3 10h18M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01'/%3E%3C/svg%3E");
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] [data-settings-reference-v2-icon="notifications"] .kg-settings-reference-v2-nav__icon {
  mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2'%3E%3Cpath d='M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9M10 21h4'/%3E%3C/svg%3E");
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] [data-settings-reference-v2-icon="workspace"] .kg-settings-reference-v2-nav__icon {
  mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2'%3E%3Crect x='3' y='3' width='18' height='18' rx='2'/%3E%3Cpath d='M9 3v18M9 9h12'/%3E%3C/svg%3E");
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] [data-settings-reference-v2-icon="portal"] .kg-settings-reference-v2-nav__icon {
  mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2'%3E%3Cpath d='M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75'/%3E%3C/svg%3E");
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] [data-settings-reference-v2-icon="payments"] .kg-settings-reference-v2-nav__icon {
  mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2'%3E%3Crect x='2' y='5' width='20' height='14' rx='2'/%3E%3Cpath d='M2 10h20M6 15h2'/%3E%3C/svg%3E");
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] [data-settings-reference-v2-icon="branding"] .kg-settings-reference-v2-nav__icon {
  mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2'%3E%3Cpath d='M12 22a10 10 0 1 1 10-10c0 2.2-1.8 4-4 4h-1.8a1.8 1.8 0 0 0-1.6 2.6l.2.4c.7 1.4-.3 3-1.8 3z'/%3E%3Ccircle cx='7.5' cy='10.5' r='.5' fill='black'/%3E%3Ccircle cx='10.5' cy='7.5' r='.5' fill='black'/%3E%3Ccircle cx='14.5' cy='7.5' r='.5' fill='black'/%3E%3Ccircle cx='17' cy='11' r='.5' fill='black'/%3E%3C/svg%3E");
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] [data-settings-reference-v2-icon="ai"] .kg-settings-reference-v2-nav__icon {
  mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2'%3E%3Cpath d='m12 3-1.4 3.6L7 8l3.6 1.4L12 13l1.4-3.6L17 8l-3.6-1.4L12 3zM19 14l-.8 2.2L16 17l2.2.8L19 20l.8-2.2L22 17l-2.2-.8L19 14zM5 13l-1 2.5L1.5 16.5 4 17.5 5 20l1-2.5 2.5-1L6 15.5 5 13z'/%3E%3C/svg%3E");
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] [data-settings-reference-v2-icon="users"] .kg-settings-reference-v2-nav__icon {
  mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2'%3E%3Ccircle cx='9' cy='8' r='4'/%3E%3Cpath d='M2 21a7 7 0 0 1 14 0M16 4a4 4 0 0 1 0 8M18 15a6 6 0 0 1 4 6'/%3E%3C/svg%3E");
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] [data-settings-reference-v2-icon="sessions"] .kg-settings-reference-v2-nav__icon {
  mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2'%3E%3Ccircle cx='12' cy='12' r='9'/%3E%3Cpath d='M12 7v5l3 2'/%3E%3C/svg%3E");
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] [data-settings-reference-v2-icon="audit"] .kg-settings-reference-v2-nav__icon {
  mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2'%3E%3Cpath d='M9 3h6l1 3h3v15H5V6h3l1-3zM9 12l2 2 4-4M9 18h6'/%3E%3C/svg%3E");
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] [data-settings-reference-v2-icon="usage"] .kg-settings-reference-v2-nav__icon {
  mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2'%3E%3Cpath d='M4 20V10M10 20V4M16 20v-7M22 20H2'/%3E%3C/svg%3E");
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] [data-settings-reference-v2-icon="archive"] .kg-settings-reference-v2-nav__icon {
  mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2'%3E%3Cpath d='M3 6h18M5 6v14h14V6M3 3h18v3H3zM9 11h6'/%3E%3C/svg%3E");
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-nav-toggle,
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-nav__close,
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-nav-backdrop {
  display: none;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-content {
  min-width: 0;
  padding: 24px 40px 64px;
  background: var(--tds-v2-canvas);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-content > .kg-settings-panels,
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-content .kg-settings-panel,
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-content .kg-settings-panel-body {
  width: 100%;
  min-width: 0;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-content .kg-settings-panel:focus {
  outline: none;
}

/* Shared page typography and card rhythm. */
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-page-header,
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__header {
  grid-column: 1 / -1;
  display: grid;
  gap: 7px;
  margin: 0 0 4px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] :where(
  .kg-settings-reference-v2-page-header,
  .tutor-desk-settings-v2__header
) h1 {
  margin: 0;
  color: var(--tds-v2-text);
  font-family: var(--ttoria-final-font-heading);
  font-size: clamp(28px, 2.3vw, 36px);
  font-weight: 760;
  letter-spacing: -0.035em;
  line-height: 1.12;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] :where(
  .kg-settings-reference-v2-page-header,
  .tutor-desk-settings-v2__header
) p {
  max-width: 760px;
  margin: 0;
  color: var(--tds-v2-muted);
  font-size: 15px;
  line-height: 1.55;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2 {
  width: 100%;
  min-width: 0;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-items: start;
  gap: 24px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__status {
  grid-column: 1 / -1;
  min-height: 0;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__status:empty {
  display: none;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-compact-section[data-ds-component="ds/sub-panel"] {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0;
  padding: 22px;
  overflow: visible;
  border: 1px solid var(--tds-v2-border);
  border-radius: 12px;
  background: var(--tds-v2-surface);
  box-shadow: var(--tds-v2-shadow);
  color: var(--tds-v2-text);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-compact-section > .ds-sub-panel__header,
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-compact-section > .ds-sub-panel__footer:empty {
  display: none;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-compact-section > .ds-sub-panel__body {
  min-width: 0;
  display: grid;
  gap: 18px;
  margin: 0;
  color: var(--tds-v2-text);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__section-copy,
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__catalog-copy {
  display: grid;
  gap: 5px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--tds-v2-border);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] :where(
  .tutor-desk-settings-v2__section-copy,
  .tutor-desk-settings-v2__catalog-copy
) :where(h3, h4) {
  margin: 0;
  color: var(--tds-v2-text);
  font-size: 17px;
  font-weight: 720;
  letter-spacing: -0.015em;
  line-height: 1.35;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] :where(
  .tutor-desk-settings-v2__section-copy,
  .tutor-desk-settings-v2__catalog-copy
) p,
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__fine-print {
  margin: 0;
  color: var(--tds-v2-muted);
  font-size: 13px;
  line-height: 1.5;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__scope {
  width: fit-content;
  margin: 5px 0 0;
  padding: 5px 9px;
  border-radius: 999px;
  background: var(--tds-v2-action-soft);
  color: var(--tds-v2-action);
  font-size: 11px;
  font-weight: 700;
  line-height: 1.35;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__form,
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__grid {
  min-width: 0;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__form > :where(
  .tutor-desk-settings-v2__section-copy,
  .tutor-desk-settings-v2__notice,
  .tutor-desk-settings-v2__grid,
  .tutor-desk-settings-v2__actions,
  .kg-settings-notice
) {
  grid-column: 1 / -1;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__field--wide,
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__fieldset,
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__actions,
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__notice {
  grid-column: 1 / -1;
}

/* Keep each independent save boundary visually explicit. */
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 2px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__actions::before {
  content: "";
  flex: 1 1 auto;
  min-width: 12px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.notifications"] .tutor-desk-settings-v2__form {
  position: relative;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.notifications"] .tutor-desk-settings-v2__section-copy {
  padding-right: 156px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.notifications"] .tutor-desk-settings-v2__form > .tutor-desk-settings-v2__actions {
  position: absolute;
  top: 0;
  right: 0;
  padding: 0;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.notifications"] .tutor-desk-settings-v2__form > .tutor-desk-settings-v2__actions::before {
  display: none;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] :where(
  .tutor-desk-settings-v2__ai-master,
  .tutor-desk-settings-v2__ai-group
) {
  min-width: 0;
  display: grid;
  align-content: start;
  gap: 7px;
  padding: 14px 16px;
  border: 1px solid var(--tds-v2-border);
  border-radius: 10px;
  background: var(--tds-v2-surface-soft);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__ai-master {
  grid-column: 1 / -1;
  background: var(--tds-v2-action-soft);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] :where(
  .tutor-desk-settings-v2__ai-master,
  .tutor-desk-settings-v2__ai-group
) > h4 {
  margin: 0;
  padding-bottom: 7px;
  border-bottom: 1px solid var(--tds-v2-border);
  color: var(--tds-v2-text);
  font-size: 13px;
  font-weight: 720;
}

/* Form controls. Native selects remain semantic and visually hidden. */
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] :where(
  .ds-input,
  .ds-textarea,
  .kg-settings-reference-v2-select
) {
  min-width: 0;
  display: grid;
  gap: 7px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] :where(
  .ds-input__label,
  .ds-textarea__label,
  .kg-settings-reference-v2-select__label,
  .tutor-desk-settings-v2__fieldset legend,
  .tutor-desk-settings-v2__file > strong
) {
  color: var(--tds-v2-text);
  font-size: 13px;
  font-weight: 680;
  line-height: 1.4;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] :where(
  .ds-input__helper,
  .ds-textarea__helper,
  .kg-settings-reference-v2-select__helper
) {
  margin: 0;
  color: var(--tds-v2-muted);
  font-size: 12px;
  line-height: 1.45;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] :where(
  .ds-input__control,
  .ds-textarea__control,
  .kg-settings-reference-v2-select__trigger
) {
  width: 100%;
  min-height: 44px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 13px;
  border: 1px solid var(--tds-v2-border-strong);
  border-radius: 9px;
  background: var(--tds-v2-surface);
  color: var(--tds-v2-text);
  box-shadow: none;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] :where(
  .ds-input__control:focus-within,
  .ds-textarea__control:focus-within,
  .kg-settings-reference-v2-select__trigger:focus-visible
) {
  outline: none;
  border-color: var(--tds-v2-action);
  box-shadow: var(--tds-v2-focus);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] :where(
  .ds-input__value,
  .ds-textarea__value
) {
  width: 100%;
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
  outline: 0;
  background: transparent;
  color: var(--tds-v2-text);
  font: inherit;
  font-size: 14px;
  line-height: 1.45;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .ds-input__value {
  /* The shared Final UI input itself is 48px tall. Settings places it inside
   * an app-drawn control shell, so retaining that inner height produced a
   * visually inflated 64px field. Keep the complete interactive shell at the
   * accessible 44px target while restoring the compact reference density. */
  min-height: 28px;
  height: 28px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .ds-textarea__value {
  min-height: 104px;
  resize: vertical;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] :where(
  .ds-input__value,
  .ds-textarea__value
)::placeholder {
  color: color-mix(in srgb, var(--tds-v2-muted) 72%, transparent);
  opacity: 1;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] :where(
  .ds-input__value,
  .ds-textarea__value
):invalid:not(:placeholder-shown) {
  color: var(--tds-v2-danger);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-select {
  position: relative;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-select__semantic,
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__semantic-file {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  border: 0;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  white-space: nowrap;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-select__trigger {
  justify-content: space-between;
  font: inherit;
  font-size: 14px;
  text-align: left;
  cursor: pointer;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-select__chevron {
  width: 8px;
  height: 8px;
  flex: 0 0 8px;
  border-right: 2px solid currentColor;
  border-bottom: 2px solid currentColor;
  transform: translateY(-2px) rotate(45deg);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-select__value {
  min-width: 0;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-select__value > span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-select__swatch {
  width: 18px;
  height: 18px;
  flex: 0 0 18px;
  overflow: visible;
  border: 1px solid var(--tds-v2-border-strong);
  border-radius: 50%;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-select[data-open="true"] .kg-settings-reference-v2-select__chevron {
  transform: translateY(2px) rotate(225deg);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-select__listbox {
  position: absolute;
  z-index: 80;
  top: calc(100% - 22px);
  left: 0;
  right: 0;
  max-height: min(310px, 46vh);
  display: grid;
  gap: 3px;
  padding: 7px;
  overflow-y: auto;
  border: 1px solid var(--tds-v2-border-strong);
  border-radius: 10px;
  background: var(--tds-v2-surface);
  box-shadow: 0 18px 45px rgba(15, 31, 59, 0.18);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-select__option {
  width: 100%;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 9px 11px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--tds-v2-text);
  font: inherit;
  font-size: 14px;
  text-align: left;
  cursor: pointer;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-select__option:hover,
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-select__option:focus-visible {
  outline: none;
  background: var(--tds-v2-surface-hover);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-select__option.is-selected {
  background: var(--tds-v2-action-soft);
  color: var(--tds-v2-action);
  font-weight: 680;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-select__option.is-selected > span {
  width: 10px;
  height: 6px;
  border-left: 2px solid currentColor;
  border-bottom: 2px solid currentColor;
  transform: rotate(-45deg) translateY(-2px);
}

/* Application-rendered time and calendar pickers replace browser chrome. */
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__temporal {
  position: relative;
  min-width: 0;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__temporal .ds-input__control {
  padding-right: 52px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__temporal-toggle {
  position: absolute;
  top: 23px;
  right: 0;
  width: 44px;
  height: 44px;
  display: grid;
  place-items: center;
  border: 0;
  border-left: 1px solid var(--tds-v2-border);
  border-radius: 0 7px 7px 0;
  background: transparent;
  color: var(--tds-v2-action);
  cursor: pointer;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__temporal-toggle > span {
  width: 18px;
  height: 18px;
  display: block;
  background: currentColor;
  mask-position: center;
  mask-repeat: no-repeat;
  mask-size: contain;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] [data-settings-reference-v2-temporal="time"] .tutor-desk-settings-v2__temporal-toggle > span {
  mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2'%3E%3Ccircle cx='12' cy='12' r='9'/%3E%3Cpath d='M12 7v5l3 2'/%3E%3C/svg%3E");
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] [data-settings-reference-v2-temporal="date"] .tutor-desk-settings-v2__temporal-toggle > span {
  mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2'%3E%3Crect x='3' y='5' width='18' height='16' rx='2'/%3E%3Cpath d='M16 3v4M8 3v4M3 10h18'/%3E%3C/svg%3E");
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__temporal-toggle:focus-visible {
  outline: 2px solid var(--tds-v2-action);
  outline-offset: 2px;
  box-shadow: var(--tds-v2-focus);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__temporal-dialog {
  position: absolute;
  z-index: 85;
  top: 70px;
  right: 0;
  width: min(340px, calc(100vw - 40px));
  display: grid;
  gap: 13px;
  padding: 14px;
  border: 1px solid var(--tds-v2-border-strong);
  border-radius: 11px;
  background: var(--tds-v2-surface);
  box-shadow: 0 18px 45px rgba(15, 31, 59, 0.2);
  color: var(--tds-v2-text);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__temporal-heading {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__temporal-heading strong {
  font-size: 15px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__temporal-heading span {
  color: var(--tds-v2-muted);
  font-size: 11px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__temporal-options {
  min-width: 0;
  display: grid;
  gap: 7px;
  margin: 0;
  padding: 0;
  border: 0;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__temporal-options legend {
  padding: 0;
  color: var(--tds-v2-muted);
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__temporal-options > div {
  max-height: 148px;
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 5px;
  overflow-y: auto;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] :where(
  .tutor-desk-settings-v2__temporal-option,
  .tutor-desk-settings-v2__calendar-day
) {
  min-width: 0;
  min-height: 44px;
  display: grid;
  place-items: center;
  padding: 6px;
  border: 1px solid transparent;
  border-radius: 7px;
  background: transparent;
  color: var(--tds-v2-text);
  font: inherit;
  font-size: 12px;
  cursor: pointer;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] :where(
  .tutor-desk-settings-v2__temporal-option,
  .tutor-desk-settings-v2__calendar-day
):where(:hover, :focus-visible) {
  outline: none;
  border-color: var(--tds-v2-border);
  background: var(--tds-v2-surface-hover);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] :where(
  .tutor-desk-settings-v2__temporal-option,
  .tutor-desk-settings-v2__calendar-day
).is-selected {
  border-color: var(--tds-v2-action);
  background: var(--tds-v2-action);
  color: var(--tds-v2-action-contrast);
  font-weight: 700;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__temporal-done {
  min-height: 44px;
  border: 1px solid var(--tds-v2-action);
  border-radius: 8px;
  background: var(--tds-v2-action);
  color: var(--tds-v2-action-contrast);
  font: inherit;
  font-size: 13px;
  font-weight: 700;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__calendar-heading {
  display: grid;
  grid-template-columns: 40px minmax(0, 1fr) 40px;
  align-items: center;
  gap: 8px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__calendar-heading strong {
  text-align: center;
  font-size: 14px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__calendar-heading button {
  min-height: 44px;
  display: grid;
  place-items: center;
  border: 1px solid var(--tds-v2-border);
  border-radius: 8px;
  background: var(--tds-v2-surface-soft);
  color: var(--tds-v2-text);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__calendar-heading button > span {
  width: 8px;
  height: 8px;
  border-left: 2px solid currentColor;
  border-bottom: 2px solid currentColor;
  transform: rotate(45deg);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__calendar-heading button:last-child > span {
  transform: rotate(225deg);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] :where(
  .tutor-desk-settings-v2__calendar-weekdays,
  .tutor-desk-settings-v2__calendar-days
) {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 4px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__calendar-weekdays {
  color: var(--tds-v2-muted);
  font-size: 10px;
  font-weight: 700;
  text-align: center;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__order-controls {
  display: flex;
  align-items: end;
  gap: 8px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__order-controls button {
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 9px 12px;
  border: 1px solid var(--tds-v2-border-strong);
  border-radius: 8px;
  background: var(--tds-v2-surface);
  color: var(--tds-v2-text);
  font: inherit;
  font-size: 12px;
  font-weight: 680;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__order-controls button > span {
  width: 8px;
  height: 8px;
  border-left: 2px solid currentColor;
  border-top: 2px solid currentColor;
  transform: translateY(2px) rotate(45deg);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__order-controls button:last-child > span {
  transform: translateY(-2px) rotate(225deg);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__order-controls button:where(:hover, :focus-visible) {
  outline: none;
  border-color: var(--tds-v2-action);
  color: var(--tds-v2-action);
  box-shadow: var(--tds-v2-focus);
}

/* Switches and multi-choice tiles. */
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2 .ds-checkbox[data-ds-component="ds/checkbox"] {
  min-width: 0;
  min-height: 52px;
  height: auto;
  align-self: start;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 44px;
  align-items: center;
  gap: 14px;
  padding: 3px 0;
  color: var(--tds-v2-text);
  cursor: pointer;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2 .ds-checkbox__content {
  grid-column: 1;
  grid-row: 1;
  min-width: 0;
  display: grid;
  gap: 3px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2 .ds-checkbox__label {
  color: var(--tds-v2-text);
  font-size: 14px;
  font-weight: 660;
  line-height: 1.4;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2 .ds-checkbox__helper {
  color: var(--tds-v2-muted);
  font-size: 12px;
  line-height: 1.45;
  opacity: 1;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2 .ds-checkbox__control {
  position: relative;
  grid-column: 2;
  grid-row: 1;
  width: 44px;
  min-width: 44px;
  height: 24px;
  display: block;
  border: 1px solid var(--tds-v2-border-strong);
  border-radius: 999px;
  background: var(--tds-v2-border-strong);
  box-shadow: none;
  transition: background-color 150ms ease, border-color 150ms ease;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2 .ds-checkbox__mark {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 18px;
  height: 18px;
  display: block;
  border-radius: 50%;
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(15, 31, 59, 0.25);
  color: transparent;
  font-size: 0;
  transform: translateX(0);
  transition: transform 150ms ease;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2 .ds-checkbox:has(.ds-checkbox__native:checked) .ds-checkbox__control {
  border-color: var(--tds-v2-action);
  background: var(--tds-v2-action);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2 .ds-checkbox:has(.ds-checkbox__native:checked) .ds-checkbox__mark {
  transform: translateX(20px);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2 .ds-checkbox:has(.ds-checkbox__native:focus-visible) .ds-checkbox__control {
  outline: 2px solid var(--tds-v2-action);
  outline-offset: 2px;
  box-shadow: var(--tds-v2-focus);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2 .ds-checkbox[aria-disabled="true"] {
  opacity: 0.55;
  cursor: not-allowed;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__fieldset {
  min-width: 0;
  display: grid;
  gap: 12px;
  margin: 0;
  padding: 16px;
  border: 1px solid var(--tds-v2-border);
  border-radius: 10px;
  background: var(--tds-v2-surface-soft);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__fieldset legend {
  padding: 0 4px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__choice-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__choice-grid .ds-checkbox {
  min-height: 58px;
  padding: 10px 12px;
  border: 1px solid var(--tds-v2-border);
  border-radius: 9px;
  background: var(--tds-v2-surface);
}

/* App-drawn segmented and option-card controls. */
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__fieldset--reference-segments,
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__fieldset--reference-cards {
  padding: 0;
  border: 0;
  background: transparent;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__segments {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(122px, 1fr));
  gap: 8px;
  padding: 4px;
  border: 1px solid var(--tds-v2-border);
  border-radius: 11px;
  background: var(--tds-v2-surface-soft);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__fieldset--reference-cards .tutor-desk-settings-v2__segments {
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 10px;
  padding: 0;
  border: 0;
  background: transparent;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__segment {
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 9px 11px;
  border: 1px solid transparent;
  border-radius: 8px;
  background: transparent;
  color: var(--tds-v2-muted);
  font: inherit;
  font-size: 13px;
  font-weight: 650;
  line-height: 1.35;
  cursor: pointer;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__fieldset--reference-cards .tutor-desk-settings-v2__segment {
  min-height: 76px;
  justify-content: flex-start;
  padding: 14px;
  border-color: var(--tds-v2-border);
  background: var(--tds-v2-surface);
  text-align: left;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__segment:hover {
  background: var(--tds-v2-surface-hover);
  color: var(--tds-v2-text);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__segment.is-selected {
  border-color: color-mix(in srgb, var(--tds-v2-action) 28%, var(--tds-v2-border));
  background: var(--tds-v2-action-soft);
  color: var(--tds-v2-action);
  box-shadow: 0 1px 4px rgba(11, 109, 248, 0.08);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__segment:focus-visible {
  outline: 2px solid var(--tds-v2-action);
  outline-offset: 2px;
  box-shadow: var(--tds-v2-focus);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__segment-icon {
  width: 14px;
  height: 14px;
  flex: 0 0 14px;
  border: 1.5px solid currentColor;
  border-radius: 50%;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__segment.is-selected .tutor-desk-settings-v2__segment-icon {
  border: 4px solid var(--tds-v2-action);
  background: var(--tds-v2-surface);
}

/* Buttons, loading, disabled, success, conflict, and error presentation. */
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .ds-button[data-ds-component="ds/button"] {
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 17px;
  border: 1px solid var(--tds-v2-border-strong);
  border-radius: 9px;
  background: var(--tds-v2-surface);
  color: var(--tds-v2-text);
  box-shadow: none;
  font: inherit;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.3;
  cursor: pointer;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .ds-button[data-td-final-action-role="primary"] {
  border-color: var(--tds-v2-action);
  background: var(--tds-v2-action);
  color: var(--tds-v2-action-contrast);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .ds-button[data-td-final-action-role="danger"] {
  border-color: color-mix(in srgb, var(--tds-v2-danger) 40%, var(--tds-v2-border));
  background: color-mix(in srgb, var(--tds-v2-danger) 10%, var(--tds-v2-surface));
  color: var(--tds-v2-danger);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .ds-button:hover:not(:disabled):not([aria-disabled="true"]) {
  border-color: var(--tds-v2-action);
  background: var(--tds-v2-surface-hover);
  color: var(--tds-v2-action);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .ds-button[data-td-final-action-role="primary"]:hover:not(:disabled):not([aria-disabled="true"]) {
  border-color: var(--tds-v2-action-hover);
  background: var(--tds-v2-action-hover);
  color: var(--tds-v2-action-contrast);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .ds-button:focus-visible {
  outline: 2px solid var(--tds-v2-action);
  outline-offset: 2px;
  box-shadow: var(--tds-v2-focus);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] :where(
  .ds-button:disabled,
  .ds-button[aria-disabled="true"],
  .kg-settings-reference-v2-select__trigger:disabled
) {
  opacity: 0.5;
  cursor: not-allowed;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] [aria-busy="true"] {
  cursor: progress;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] [aria-busy="true"]::before {
  content: "";
  width: 15px;
  height: 15px;
  flex: 0 0 15px;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: tds-settings-reference-v2-spin 700ms linear infinite;
}

@keyframes tds-settings-reference-v2-spin {
  to { transform: rotate(360deg); }
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-notice[data-ds-component="ds/alert"] {
  display: grid;
  gap: 5px;
  padding: 13px 15px;
  border: 1px solid var(--tds-v2-border);
  border-left: 3px solid var(--tds-v2-action);
  border-radius: 9px;
  background: var(--tds-v2-action-soft);
  color: var(--tds-v2-text);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-notice.ds-alert--intent-error {
  border-left-color: var(--tds-v2-danger);
  background: color-mix(in srgb, var(--tds-v2-danger) 8%, var(--tds-v2-surface));
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-notice.ds-alert--intent-success {
  border-left-color: var(--tds-v2-success);
  background: color-mix(in srgb, var(--tds-v2-success) 8%, var(--tds-v2-surface));
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-notice.ds-alert--intent-warning {
  border-left-color: var(--tds-v2-warning);
  background: color-mix(in srgb, var(--tds-v2-warning) 9%, var(--tds-v2-surface));
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-notice :where(.ds-alert__label, .ds-alert__body) {
  margin: 0;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-notice .ds-alert__label {
  color: var(--tds-v2-text);
  font-size: 13px;
  font-weight: 720;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-notice .ds-alert__body {
  color: var(--tds-v2-muted);
  font-size: 12px;
  line-height: 1.5;
}

/* Appearance reference cards. */
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-appearance-panel--previews {
  margin-top: 24px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-appearance-panel--previews > .ds-sub-panel__body {
  gap: 18px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-appearance-panel--previews > .ds-sub-panel__header {
  display: flex;
  padding: 20px 20px 0;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-appearance-panel--previews > .ds-sub-panel__body {
  flex: 0 0 auto;
  gap: 18px;
  padding: 14px 20px 20px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-final-theme-intro {
  display: none;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-pack-grid--appearance {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-final-theme-card {
  position: relative;
  min-width: 0;
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  grid-template-rows: 112px auto;
  justify-content: initial;
  gap: 12px;
  padding: 10px;
  border: 1px solid var(--tds-v2-border);
  border-radius: 11px;
  background: var(--tds-v2-surface-soft);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-final-theme-card > [data-action="appearance-pick"] {
  position: absolute;
  inset: 0;
  z-index: 2;
  width: 100%;
  height: 100%;
  max-width: none;
  min-height: 44px;
  margin: 0;
  padding: 0;
  border: 0;
  opacity: 0;
  cursor: pointer;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-final-theme-card:has(> [data-action="appearance-pick"]:focus-visible) {
  outline: 2px solid var(--tds-v2-action);
  outline-offset: 2px;
  box-shadow: var(--tds-v2-focus);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-final-theme-card.active {
  border-color: var(--tds-v2-action);
  box-shadow: var(--tds-v2-focus);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-final-theme-card.active::after {
  content: "\\2713";
  position: absolute;
  top: 18px;
  right: 18px;
  z-index: 1;
  width: 28px;
  height: 28px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: var(--tds-v2-action);
  color: var(--tds-v2-action-contrast);
  font-size: 16px;
  font-weight: 800;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-final-theme-preview {
  grid-column: 1 / -1;
  width: 100%;
  height: 112px;
  min-height: 112px;
  max-height: 112px;
  display: grid;
  grid-template-columns: 92px minmax(0, 1fr);
  overflow: hidden;
  border: 1px solid var(--tds-v2-border);
  border-radius: 8px;
  background: var(--tds-v2-surface);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-final-theme-preview__rail,
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-final-theme-preview__canvas {
  display: grid;
  align-content: start;
  gap: 7px;
  padding: 8px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-final-theme-preview__rail {
  border-right: 1px solid var(--tds-v2-border);
  background: color-mix(in srgb, var(--tds-v2-surface-soft) 88%, var(--tds-v2-action));
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-final-theme-preview__wordmark {
  position: relative;
  width: 100%;
  height: 48px;
  min-height: 48px;
  overflow: hidden;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-final-theme-preview__wordmark img {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 106px;
  height: auto;
  max-width: none;
  transform: translate(-50%, -50%);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-final-theme-preview__rail-line,
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-final-theme-preview__bar,
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-final-theme-preview__card {
  display: block;
  border-radius: 4px;
  background: var(--tds-v2-border);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-final-theme-preview__rail-line {
  height: 7px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-final-theme-preview__rail-line--active {
  background: var(--tds-v2-action);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-final-theme-preview__bar {
  height: 15px;
  background: var(--tds-v2-surface-soft);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-final-theme-preview__cards {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 7px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-final-theme-preview__card {
  min-height: 31px;
  border: 1px solid var(--tds-v2-border);
  background: var(--tds-v2-surface-soft);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-final-theme-card__heading {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 7px;
  color: var(--tds-v2-text);
  font-size: 14px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-final-theme-badge {
  padding: 3px 7px;
  border-radius: 999px;
  background: var(--tds-v2-action-soft);
  color: var(--tds-v2-action);
  font-size: 10px;
  font-weight: 750;
  text-transform: uppercase;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-appearance-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 10px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-appearance-contributions {
  display: grid;
  gap: 24px;
  margin-top: 24px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-panel[data-panel="appearance"] .kg-settings-appearance-panel--previews {
  min-height: 0;
  height: auto;
  flex: 0 0 auto;
  align-self: start;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-appearance-contribution[data-ds-component="ds/sub-panel"] {
  padding: 0;
  border: 0;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-appearance-contribution > .ds-sub-panel__body {
  display: block;
  padding: 0;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-appearance-contribution .tutor-desk-settings-v2__header {
  display: none;
}

/* Catalog, file upload, branding history, and empty/loading containers. */
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__catalog {
  grid-column: 1 / -1;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] :where(
  .tutor-desk-settings-v2__catalog-list,
  .tutor-desk-settings-v2__branding-versions
) {
  display: grid;
  gap: 10px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] :where(
  .tutor-desk-settings-v2__catalog-item,
  .tutor-desk-settings-v2__branding-version
) {
  min-width: 0;
  padding: 0;
  overflow: hidden;
  border: 1px solid var(--tds-v2-border);
  border-radius: 10px;
  background: var(--tds-v2-surface-soft);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__catalog-disclosure {
  width: 100%;
  min-height: 48px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 11px 14px;
  border: 0;
  background: transparent;
  color: var(--tds-v2-text);
  font: inherit;
  font-size: 14px;
  font-weight: 680;
  text-align: left;
  cursor: pointer;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__catalog-disclosure:hover {
  background: var(--tds-v2-surface-hover);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__catalog-disclosure:focus-visible {
  outline: 2px solid var(--tds-v2-action);
  outline-offset: -3px;
  box-shadow: var(--tds-v2-focus);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__catalog-disclosure > span:last-child {
  width: 9px;
  height: 9px;
  flex: 0 0 9px;
  border-right: 2px solid currentColor;
  border-bottom: 2px solid currentColor;
  transform: translateY(-2px) rotate(45deg);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__catalog-disclosure[aria-expanded="true"] > span:last-child {
  transform: translateY(2px) rotate(225deg);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__catalog-item > form {
  margin: 0;
  padding: 16px;
  border-top: 1px solid var(--tds-v2-border);
  background: var(--tds-v2-surface);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__file {
  position: relative;
  display: grid;
  gap: 7px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__upload-surface {
  min-height: 132px;
  display: grid;
  place-items: center;
  align-content: center;
  gap: 6px;
  padding: 20px;
  border: 1px dashed var(--tds-v2-border-strong);
  border-radius: 10px;
  background: var(--tds-v2-surface-soft);
  color: var(--tds-v2-muted);
  text-align: center;
  cursor: pointer;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .tutor-desk-settings-v2__file:has(.tutor-desk-settings-v2__semantic-file:focus-visible) .tutor-desk-settings-v2__upload-surface {
  outline: 2px solid var(--tds-v2-action);
  outline-offset: 2px;
  border-color: var(--tds-v2-action);
  box-shadow: var(--tds-v2-focus);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__upload-icon {
  width: 28px;
  height: 28px;
  background: var(--tds-v2-action);
  mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2'%3E%3Cpath d='M12 16V4M7 9l5-5 5 5M5 20h14'/%3E%3C/svg%3E") center / contain no-repeat;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__upload-title {
  color: var(--tds-v2-action);
  font-size: 14px;
  font-weight: 700;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__upload-file-name {
  max-width: 100%;
  overflow: hidden;
  color: var(--tds-v2-muted);
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__branding-workflow,
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.branding"] > .kg-settings-compact-section,
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.notifications"] > .kg-settings-compact-section,
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.workspace-views"] > .kg-settings-compact-section,
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.ai-automation"] > .kg-settings-compact-section {
  grid-column: 1 / -1;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__branding-version {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(160px, 260px) auto;
  align-items: center;
  gap: 16px;
  padding: 14px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__branding-version .tutor-desk-settings-v2__catalog-copy {
  padding: 0;
  border: 0;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__logo-preview {
  display: block;
  max-width: min(100%, 320px);
  max-height: 150px;
  object-fit: contain;
  border: 1px solid var(--tds-v2-border);
  border-radius: 9px;
  background: var(--tds-v2-surface-soft);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-empty,
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] [data-settings-v2-subjects-state="empty"] {
  min-height: 112px;
  display: grid;
  place-items: center;
  padding: 20px;
  border: 1px dashed var(--tds-v2-border-strong);
  border-radius: 10px;
  background: var(--tds-v2-surface-soft);
  color: var(--tds-v2-muted);
  text-align: center;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-layout {
  grid-template-columns: 296px minmax(0, 1fr);
  min-height: calc(100dvh - 86px);
  border: 1px solid var(--tds-v2-border);
  border-radius: 14px;
  /* The outer plugin region is the governed vertical scroll owner. Clipping
   * this grid hid lower Settings controls without contributing their height
   * to that scrollport, so real controls could never be reached. */
  overflow: visible;
  background: var(--tds-v2-canvas);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-nav {
  top: 86px;
  width: 296px;
  height: calc(100dvh - 102px);
  border-right-color: var(--tds-v2-border);
  background: var(--tds-v2-surface);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-nav__scroll {
  gap: 18px;
  padding: 30px 16px 24px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-nav__group {
  gap: 6px;
  padding-bottom: 16px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-nav__item {
  min-height: 44px;
  grid-template-columns: 22px minmax(0, 1fr);
  gap: 11px;
  padding: 8px 11px;
  border-radius: 10px;
  font-size: 13.5px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-nav__item.is-active {
  border-color: color-mix(in srgb, var(--tds-v2-action) 72%, var(--tds-v2-border));
  background: linear-gradient(135deg, var(--tds-v2-action), color-mix(in srgb, var(--tds-v2-action) 74%, #4f46e5));
  color: var(--tds-v2-action-contrast);
  box-shadow: 0 8px 18px color-mix(in srgb, var(--tds-v2-action) 18%, transparent);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-content {
  padding: 26px 34px 54px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2 {
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: 16px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__header {
  grid-column: 1 / -1;
  grid-template-columns: 44px minmax(0, 1fr);
  align-items: center;
  gap: 13px;
  min-height: 62px;
  margin: 0 0 2px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__header-icon {
  width: 44px;
  height: 44px;
  display: grid;
  place-items: center;
  border: 1px solid color-mix(in srgb, var(--tds-v2-action) 28%, var(--tds-v2-border));
  border-radius: 12px;
  background: linear-gradient(145deg, var(--tds-v2-action-soft), color-mix(in srgb, var(--tds-v2-surface) 80%, var(--tds-v2-action) 20%));
  color: var(--tds-v2-action);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__header-icon svg {
  width: 23px;
  height: 23px;
  stroke-width: 1.9;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__header-copy {
  min-width: 0;
  display: grid;
  gap: 2px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__eyebrow {
  color: var(--tds-v2-action);
  font-size: 10.5px;
  font-weight: 760;
  letter-spacing: .1em;
  line-height: 1.2;
  text-transform: uppercase;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__header h1 {
  font-size: clamp(27px, 2.15vw, 34px);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__header p {
  font-size: 13.5px;
  line-height: 1.42;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__status {
  grid-column: 1 / -1;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-compact-section[data-ds-component="ds/sub-panel"] {
  padding: 18px;
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(21, 39, 73, .035), 0 10px 26px rgba(21, 39, 73, .025);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-compact-section > .ds-sub-panel__body {
  gap: 14px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__form,
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__grid {
  gap: 14px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__section-copy,
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__catalog-copy {
  gap: 3px;
  padding-bottom: 11px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__scope {
  margin-top: 3px;
  padding: 3px 7px;
  font-size: 10px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__visual-group {
  min-width: 0;
  grid-column: 1 / -1;
  display: grid;
  align-content: start;
  gap: 12px;
  padding: 14px;
  border: 1px solid var(--tds-v2-border);
  border-radius: 11px;
  background: var(--tds-v2-surface-soft);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__visual-group--hero {
  border-color: color-mix(in srgb, var(--tds-v2-action) 32%, var(--tds-v2-border));
  background: linear-gradient(135deg, var(--tds-v2-action-soft), var(--tds-v2-surface));
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__visual-group-header,
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__ai-group-heading {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 10px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__visual-group-icon,
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__ai-group-heading > span[aria-hidden="true"] {
  width: 32px;
  height: 32px;
  flex: 0 0 32px;
  display: grid;
  place-items: center;
  border-radius: 9px;
  background: var(--tds-v2-action-soft);
  color: var(--tds-v2-action);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__visual-group-icon svg,
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__ai-group-heading svg {
  width: 18px;
  height: 18px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__visual-group-header h4,
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__ai-group-heading h4 {
  margin: 0;
  color: var(--tds-v2-text);
  font-size: 14px;
  font-weight: 730;
  line-height: 1.35;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__visual-group-header p,
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__ai-group-heading p {
  margin: 2px 0 0;
  color: var(--tds-v2-muted);
  font-size: 11.5px;
  line-height: 1.4;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__visual-group-grid {
  min-width: 0;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 12px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__field--reference-v2 {
  min-width: 0;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__field--with-icon {
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr);
  align-items: start;
  gap: 10px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__field-icon {
  width: 34px;
  height: 34px;
  display: grid;
  place-items: center;
  margin-top: 1px;
  border-radius: 9px;
  background: var(--tds-v2-action-soft);
  color: var(--tds-v2-action);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__field-icon svg {
  width: 18px;
  height: 18px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__field-control,
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__select-host {
  min-width: 0;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] :where(
  .ds-input__control,
  .ds-textarea__control,
  .kg-settings-reference-v2-select__trigger
) {
  min-height: 40px;
  padding: 8px 11px;
  border-radius: 8px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-select__trigger {
  min-height: 44px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] :where(
  .ds-input__label,
  .ds-textarea__label,
  .kg-settings-reference-v2-select__label,
  .tutor-desk-settings-v2__fieldset legend
) {
  font-size: 12.5px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] :where(
  .ds-input__helper,
  .ds-textarea__helper,
  .kg-settings-reference-v2-select__helper
) {
  font-size: 11px;
  line-height: 1.35;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__fieldset--choices {
  padding: 0;
  border: 0;
  background: transparent;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__choice-tile {
  position: relative;
  min-width: 0;
  min-height: 84px;
  display: grid;
  grid-template-rows: 30px minmax(0, 1fr);
  align-content: center;
  gap: 5px;
  padding: 10px;
  border: 1px solid var(--tds-v2-border);
  border-radius: 9px;
  background: var(--tds-v2-surface);
  color: var(--tds-v2-text);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__choice-tile:has(.ds-checkbox__native:checked) {
  border-color: var(--tds-v2-action);
  background: color-mix(in srgb, var(--tds-v2-action-soft) 72%, var(--tds-v2-surface));
  box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--tds-v2-action) 36%, transparent);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__choice-icon {
  display: grid;
  place-items: center;
  color: var(--tds-v2-action);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__choice-icon svg {
  width: 25px;
  height: 25px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__choice-tile .ds-checkbox {
  min-height: 28px;
  grid-template-columns: minmax(0, 1fr) 24px;
  gap: 6px;
  padding: 0;
  border: 0;
  background: transparent;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__choice-tile .ds-checkbox__label {
  font-size: 12px;
  text-align: center;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__choice-tile .ds-checkbox__control {
  width: 22px;
  min-width: 22px;
  height: 22px;
  border-radius: 50%;
  background: var(--tds-v2-surface-soft);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__choice-tile .ds-checkbox__mark {
  top: 1px;
  left: 1px;
  width: 18px;
  height: 18px;
  transform: none;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__choice-tile .ds-checkbox:has(.ds-checkbox__native:checked) .ds-checkbox__mark {
  transform: none;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] [data-settings-reference-v2-choices="hiddenSidebarShortcuts"] .tutor-desk-settings-v2__choice-grid {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] [data-settings-reference-v2-choices="hiddenSidebarShortcuts"] .tutor-desk-settings-v2__choice-tile {
  min-height: 108px;
  align-content: center;
  justify-items: center;
  gap: 8px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] [data-settings-reference-v2-choices="hiddenSidebarShortcuts"] .tutor-desk-settings-v2__choice-icon svg {
  width: 29px;
  height: 29px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] [data-settings-reference-v2-choices="hiddenSidebarShortcuts"] .tutor-desk-settings-v2__choice-tile .ds-checkbox {
  width: 100%;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: minmax(32px, auto) 24px;
  justify-items: center;
  gap: 7px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] [data-settings-reference-v2-choices="hiddenSidebarShortcuts"] .tutor-desk-settings-v2__choice-tile .ds-checkbox__content {
  grid-column: 1;
  grid-row: 1;
  align-items: center;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] [data-settings-reference-v2-choices="hiddenSidebarShortcuts"] .tutor-desk-settings-v2__choice-tile .ds-checkbox__control {
  grid-column: 1;
  grid-row: 2;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] [data-settings-reference-v2-choices="hiddenSidebarShortcuts"] .tutor-desk-settings-v2__choice-tile .ds-checkbox__label {
  min-width: 0;
  align-self: center;
  overflow-wrap: normal;
  word-break: normal;
  hyphens: none;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] [data-settings-reference-v2-choices="allowedDurationMinutes"] .tutor-desk-settings-v2__choice-grid {
  grid-template-columns: repeat(6, minmax(0, 1fr));
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] [data-settings-reference-v2-choices="allowedLessonTypeIds"] .tutor-desk-settings-v2__choice-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__segment-icon {
  width: 26px;
  height: 26px;
  display: grid;
  place-items: center;
  border: 0;
  border-radius: 7px;
  background: var(--tds-v2-action-soft);
  color: var(--tds-v2-action);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__segment-icon svg {
  width: 17px;
  height: 17px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__segment.is-selected .tutor-desk-settings-v2__segment-icon {
  background: var(--tds-v2-action);
  color: var(--tds-v2-action-contrast);
}

/* Profile: two-column identity composition with currency directly below the
 * personal profile and the shared business profile spanning the right side. */
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.profile-business"] > [data-settings-v2-resource="personal:profile"],
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.profile-business"] > [data-settings-v2-resource="tenant:billing"] {
  grid-column: 1 / span 5;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.profile-business"] > [data-settings-v2-resource="tenant:profile"] {
  grid-column: 6 / -1;
  grid-row: 3 / span 2;
}

/* Schedule: lesson defaults, illustrated availability, then the real catalog. */
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.schedule-defaults"] > [data-settings-v2-resource="tenant:schedule"] {
  grid-column: 1 / span 8;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.schedule-defaults"] > [data-settings-v2-resource="tenant:schedule"] .tutor-desk-settings-v2__form {
  position: relative;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.schedule-defaults"] > [data-settings-v2-resource="tenant:schedule"] .tutor-desk-settings-v2__section-copy {
  padding-right: 180px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.schedule-defaults"] > [data-settings-v2-resource="tenant:schedule"] .tutor-desk-settings-v2__form > .tutor-desk-settings-v2__actions {
  position: absolute;
  z-index: 2;
  top: 0;
  right: 0;
  padding: 0;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.schedule-defaults"] > [data-settings-v2-resource="tenant:schedule"] .tutor-desk-settings-v2__form > .tutor-desk-settings-v2__actions::before {
  display: none;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.schedule-defaults"] > [data-settings-v2-resource="tenant:schedule"] .tutor-desk-settings-v2__form > .tutor-desk-settings-v2__actions .ds-button {
  min-width: 160px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.schedule-defaults"] > [data-settings-reference-v2-card="availability"] {
  grid-column: 9 / -1;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.schedule-defaults"] > [data-settings-v2-catalog="lesson_type"] {
  grid-column: 1 / -1;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__availability-card > .ds-sub-panel__body {
  justify-items: center;
  text-align: center;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__availability-illustration {
  width: min(100%, 210px);
  color: var(--tds-v2-action);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__availability-illustration svg {
  width: 100%;
  height: auto;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__availability-card .tutor-desk-settings-v2__section-copy {
  border: 0;
  padding: 0;
  text-align: left;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__availability-card .tutor-desk-settings-v2__actions {
  width: 100%;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__availability-card .tutor-desk-settings-v2__actions .ds-button {
  width: 100%;
}

/* Single-resource pages use their inner visual groups as the visible cards. */
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] :where(
  .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.notifications"],
  .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.workspace-views"],
  .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.ai-automation"]
) > [data-settings-v2-resource] {
  grid-column: 1 / -1;
  padding: 0;
  border: 0;
  background: transparent;
  box-shadow: none;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] :where(
  .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.notifications"],
  .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.workspace-views"],
  .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.ai-automation"]
) > [data-settings-v2-resource] .tutor-desk-settings-v2__section-copy {
  display: none;
}

/* Notification rows retain one real form and one save boundary. */
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.notifications"] [data-settings-v2-resource="tenant:notifications"] .tutor-desk-settings-v2__grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.notifications"] :where(
  [data-settings-reference-v2-group="delivery"],
  [data-settings-reference-v2-group="quiet-hours"]
) {
  grid-column: 1 / -1;
}

/* Workspace mirrors the reference hierarchy: launch, shortcut tiles, then
 * compact calendar/privacy and default-view matrices. */
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.workspace-views"] [data-settings-v2-resource="personal:workspace"] .tutor-desk-settings-v2__grid {
  grid-template-columns: repeat(12, minmax(0, 1fr));
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.workspace-views"] [data-settings-v2-resource="personal:workspace"] .tutor-desk-settings-v2__form {
  position: relative;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.workspace-views"] [data-settings-v2-resource="personal:workspace"] .tutor-desk-settings-v2__form > .tutor-desk-settings-v2__actions {
  position: absolute;
  z-index: 2;
  top: -92px;
  right: 0;
  padding: 0;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.workspace-views"] [data-settings-v2-resource="personal:workspace"] .tutor-desk-settings-v2__form > .tutor-desk-settings-v2__actions::before {
  display: none;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.workspace-views"] [data-settings-v2-resource="personal:workspace"] .tutor-desk-settings-v2__form > .tutor-desk-settings-v2__actions .ds-button {
  min-width: 160px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.workspace-views"] [data-settings-reference-v2-group="opening"] {
  grid-column: 1 / span 5;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.workspace-views"] [data-settings-reference-v2-group="shortcuts"] {
  grid-column: 6 / -1;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.workspace-views"] [data-settings-reference-v2-group="calendar-privacy"] {
  grid-column: 1 / span 4;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.workspace-views"] [data-settings-reference-v2-group="default-views"] {
  grid-column: 5 / -1;
}

/* Student Portal: defaults, policy, choice cards, then the real catalogs. */
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.students-portal"] > [data-settings-v2-resource] {
  grid-column: 1 / -1;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.students-portal"] [data-settings-v2-resource="tenant:portal"] {
  padding: 0;
  border: 0;
  background: transparent;
  box-shadow: none;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.students-portal"] [data-settings-v2-resource="tenant:portal"] .tutor-desk-settings-v2__section-copy {
  display: none;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.students-portal"] [data-settings-v2-resource="tenant:portal"] .tutor-desk-settings-v2__grid {
  grid-template-columns: repeat(12, minmax(0, 1fr));
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.students-portal"] [data-settings-reference-v2-group="portal-access"] {
  grid-column: 1 / -1;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.students-portal"] [data-settings-reference-v2-group="booking-policy"] {
  grid-column: 1 / span 7;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.students-portal"] [data-settings-reference-v2-group="student-visibility"] {
  grid-column: 8 / -1;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.students-portal"] [data-settings-reference-v2-group="booking-choices"] {
  grid-column: 1 / -1;
}

@media (min-width: 1280px) {
  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.students-portal"] [data-settings-v2-resource="tenant:portal"] .tutor-desk-settings-v2__form {
    position: relative;
  }

  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.students-portal"] [data-settings-reference-v2-group="portal-access"] {
    padding-right: 250px;
  }

  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.students-portal"] [data-settings-v2-resource="tenant:portal"] .tutor-desk-settings-v2__form > .tutor-desk-settings-v2__actions {
    position: absolute;
    z-index: 2;
    top: 12px;
    right: 68px;
    padding: 0;
  }

  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.students-portal"] [data-settings-v2-resource="tenant:portal"] .tutor-desk-settings-v2__form > .tutor-desk-settings-v2__actions::before {
    display: none;
  }

  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.students-portal"] [data-settings-v2-resource="tenant:portal"] .tutor-desk-settings-v2__form > .tutor-desk-settings-v2__actions .ds-button {
    min-width: 160px;
  }
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.students-portal"] > [data-settings-v2-catalog] {
  grid-column: span 4;
}

/* Payments: one unchanged billing form, visually arranged as the reference
 * card matrix; invoice notification remains its own explicit save scope. */
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.payments-invoicing"] > [data-settings-v2-resource="tenant:billing"] {
  grid-column: 1 / -1;
  padding: 0;
  border: 0;
  background: transparent;
  box-shadow: none;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.payments-invoicing"] > [data-settings-v2-resource="tenant:billing"] .tutor-desk-settings-v2__section-copy {
  display: none;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.payments-invoicing"] [data-settings-v2-resource="tenant:billing"] .tutor-desk-settings-v2__grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-items: start;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.payments-invoicing"] [data-settings-v2-resource="tenant:billing"] .tutor-desk-settings-v2__form {
  position: relative;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.payments-invoicing"] [data-settings-v2-resource="tenant:billing"] .tutor-desk-settings-v2__form > .tutor-desk-settings-v2__actions {
  position: absolute;
  z-index: 2;
  top: -92px;
  right: 0;
  padding: 0;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.payments-invoicing"] [data-settings-v2-resource="tenant:billing"] .tutor-desk-settings-v2__form > .tutor-desk-settings-v2__actions .ds-button {
  width: auto;
  min-width: 160px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.payments-invoicing"] [data-settings-reference-v2-group="payment-default"] {
  grid-column: 1 / -1;
  grid-template-columns: minmax(0, 1fr) minmax(220px, 320px);
  align-items: center;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.payments-invoicing"] [data-settings-reference-v2-group="payment-default"] .tutor-desk-settings-v2__field--with-icon {
  display: block;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.payments-invoicing"] [data-settings-reference-v2-group="payment-default"] .tutor-desk-settings-v2__field-icon {
  display: none;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.payments-invoicing"] > [data-settings-v2-catalog="payment_method"] {
  grid-column: 1 / span 6;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.payments-invoicing"] > [data-settings-v2-resource="tenant:notifications"] {
  grid-column: 7 / -1;
}

/* Branding keeps the text preferences and the governed asset lifecycle as two
 * real surfaces, while placing both within one compact visual rhythm. */
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.branding"] > [data-settings-v2-resource="tenant:branding"],
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.branding"] > .tutor-desk-settings-v2__branding-workflow {
  grid-column: 1 / -1;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.branding"] [data-settings-v2-resource="tenant:branding"] .tutor-desk-settings-v2__grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

/* AI hero and three real preference groups. */
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__ai-master {
  grid-column: 1 / -1;
  grid-template-columns: minmax(190px, 28%) minmax(0, 1fr);
  align-items: stretch;
  gap: 0;
  padding: 0;
  overflow: hidden;
  border-color: color-mix(in srgb, var(--tds-v2-action) 28%, var(--tds-v2-border));
  background: var(--tds-v2-surface);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__ai-illustration {
  min-height: 170px;
  display: grid;
  place-items: center;
  padding: 16px;
  background: radial-gradient(circle at 30% 20%, color-mix(in srgb, #ffffff 46%, transparent), transparent 42%), linear-gradient(135deg, color-mix(in srgb, var(--tds-v2-action) 36%, var(--tds-v2-surface)), color-mix(in srgb, #8b5cf6 28%, var(--tds-v2-surface)));
  color: var(--tds-v2-action-contrast);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__ai-illustration svg {
  width: min(100%, 240px);
  height: auto;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__ai-master-content {
  min-width: 0;
  display: grid;
  align-content: center;
  gap: 14px;
  padding: 22px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__ai-group {
  padding: 14px;
  background: var(--tds-v2-surface);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__ai-group:nth-of-type(2) {
  grid-column: 1 / span 7;
  grid-row: span 2;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__ai-group:nth-of-type(3),
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__ai-group:nth-of-type(4) {
  grid-column: 8 / -1;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] :where(.tutor-desk-settings-v2__ai-master, .tutor-desk-settings-v2__ai-group) > .tutor-desk-settings-v2__field--reference-v2 {
  padding-top: 9px;
  border-top: 1px solid var(--tds-v2-border);
}

/* Final desktop composition corrections discovered in the rendered gallery. */
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-appearance-contribution .tutor-desk-settings-v2 {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-appearance-contribution .tutor-desk-settings-v2 > [data-settings-v2-resource] {
  grid-column: auto;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.profile-business"] {
  grid-template-columns: minmax(0, 5fr) minmax(0, 7fr);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.profile-business"] > [data-settings-v2-resource="personal:profile"],
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.profile-business"] > [data-settings-v2-resource="tenant:billing"] {
  grid-column: 1;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.profile-business"] > [data-settings-v2-resource="tenant:profile"] {
  grid-column: 2;
  grid-row: span 2;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.profile-business"]:has(> [data-settings-v2-resource="tenant:profile"][data-settings-v2-resource-state="restricted"]) > [data-settings-v2-resource="personal:profile"] {
  grid-column: 1;
  grid-row: auto;
  order: 1;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.profile-business"]:has(> [data-settings-v2-resource="tenant:profile"][data-settings-v2-resource-state="restricted"]) > [data-settings-v2-resource="tenant:billing"] {
  grid-column: 2;
  grid-row: auto;
  order: 1;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.profile-business"]:has(> [data-settings-v2-resource="tenant:profile"][data-settings-v2-resource-state="restricted"]) > [data-settings-v2-resource="tenant:profile"] {
  grid-column: 1 / -1;
  grid-row: auto;
  order: 2;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.profile-business"] [data-settings-v2-resource] .tutor-desk-settings-v2__grid {
  grid-template-columns: minmax(0, 1fr);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] [data-settings-reference-v2-segmented="defaultLessonDurationMinutes"] .tutor-desk-settings-v2__segments {
  grid-template-columns: repeat(6, minmax(0, 1fr));
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] :where(
  .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.notifications"],
  .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.payments-invoicing"]
) .tutor-desk-settings-v2__visual-group {
  grid-column: auto;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.notifications"] :where(
  [data-settings-reference-v2-group="delivery"],
  [data-settings-reference-v2-group="quiet-hours"]
) {
  grid-column: 1 / -1;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.ai-automation"] [data-settings-v2-resource="tenant:ai"] .tutor-desk-settings-v2__grid {
  grid-template-columns: repeat(12, minmax(0, 1fr));
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.students-portal"] > [data-settings-v2-resource="tenant:schedule"] .tutor-desk-settings-v2__form {
  grid-template-columns: minmax(220px, 1.1fr) minmax(280px, 1fr) auto;
  align-items: center;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.students-portal"] > [data-settings-v2-resource="tenant:schedule"] .tutor-desk-settings-v2__form > .tutor-desk-settings-v2__section-copy {
  grid-column: 1;
  padding: 0;
  border: 0;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.students-portal"] > [data-settings-v2-resource="tenant:schedule"] .tutor-desk-settings-v2__form > .tutor-desk-settings-v2__grid {
  grid-column: 2;
  grid-template-columns: minmax(0, 1fr);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.students-portal"] > [data-settings-v2-resource="tenant:schedule"] .tutor-desk-settings-v2__form > .tutor-desk-settings-v2__actions {
  grid-column: 3;
  padding: 0;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] :where(
  .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.notifications"],
  .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.branding"],
  .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.ai-automation"]
) [data-settings-v2-resource] .tutor-desk-settings-v2__form {
  position: relative;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] :where(
  .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.notifications"],
  .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.branding"],
  .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.ai-automation"]
) [data-settings-v2-resource] .tutor-desk-settings-v2__form > .tutor-desk-settings-v2__actions {
  position: absolute;
  z-index: 2;
  top: -92px;
  right: 0;
  padding: 0;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] :where(
  .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.notifications"],
  .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.branding"],
  .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.ai-automation"]
) [data-settings-v2-resource] .tutor-desk-settings-v2__form > .tutor-desk-settings-v2__actions .ds-button {
  width: auto;
  min-width: 160px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__visual-group {
  gap: 10px;
  padding: 12px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__choice-tile {
  min-height: 72px;
  padding: 8px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] :where(
  .kg-settings-empty,
  [data-settings-v2-subjects-state="empty"]
) {
  min-height: 72px;
  padding: 12px 16px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] [data-settings-v2-subjects-state="empty"] {
  min-height: 72px;
  padding: 12px 16px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.notifications"] [data-settings-v2-resource="tenant:notifications"] .tutor-desk-settings-v2__form > .tutor-desk-settings-v2__actions {
  top: -92px;
  right: 0;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.notifications"] .tutor-desk-settings-v2__visual-group-grid > .tutor-desk-settings-v2__field--wide {
  grid-column: auto;
}

/* Notification cards use their real field labels as the visible copy. Keep the
 * composition heading available to assistive technology without repeating the
 * same title visually and forcing the final row below the viewport. */
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.notifications"] .tutor-desk-settings-v2__visual-group-header {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  white-space: nowrap;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.notifications"] .kg-settings-notice .ds-alert__body {
  display: grid;
  gap: 4px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.notifications"] .kg-settings-notice .ds-alert__body p {
  margin: 0;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.branding"] [data-settings-reference-v2-group="brand-messages"] .tutor-desk-settings-v2__visual-group-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.branding"] [data-settings-reference-v2-group="brand-messages"] .tutor-desk-settings-v2__field--wide {
  grid-column: auto;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.branding"] [data-settings-v2-field="portalWelcomeMessage"] .ds-textarea__control {
  min-height: 58px;
  height: auto;
  max-height: none;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.branding"] [data-settings-v2-field="portalWelcomeMessage"] .ds-textarea__native {
  min-height: 58px;
  height: 58px;
  max-height: none;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.branding"] [data-settings-v2-field="brandingMode"] .tutor-desk-settings-v2__fieldset > legend,
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.workspace-views"] [data-settings-v2-field="openingPage"] .kg-settings-reference-v2-select__label,
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.ai-automation"] .tutor-desk-settings-v2__ai-master .tutor-desk-settings-v2__ai-group-heading {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  white-space: nowrap;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.payments-invoicing"] :where(
  [data-settings-v2-field="taxEnabled"],
  [data-settings-v2-field="invoicingEnabled"],
  [data-settings-v2-field="invoiceLateFeeEnabled"]
) .tutor-desk-settings-v2__field-icon,
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.students-portal"] [data-settings-v2-field="portalEnabled"] .tutor-desk-settings-v2__field-icon {
  display: none;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.payments-invoicing"] :where(
  [data-settings-v2-field="taxEnabled"],
  [data-settings-v2-field="invoicingEnabled"],
  [data-settings-v2-field="invoiceLateFeeEnabled"]
).tutor-desk-settings-v2__field--with-icon,
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.students-portal"] [data-settings-v2-field="portalEnabled"].tutor-desk-settings-v2__field--with-icon {
  grid-template-columns: minmax(0, 1fr);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.payments-invoicing"] .tutor-desk-settings-v2__visual-group-grid :where(
  .ds-input__helper,
  .kg-settings-reference-v2-select__helper
) {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  white-space: nowrap;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.workspace-views"] [data-settings-reference-v2-group="opening"] [data-settings-v2-field="openingPage"] .kg-settings-reference-v2-select__helper {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  white-space: nowrap;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-appearance-panel--previews {
  margin-top: 12px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-final-theme-card {
  grid-template-rows: 92px auto;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-final-theme-preview {
  height: 92px;
  min-height: 92px;
  max-height: 92px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-appearance-contributions {
  gap: 16px;
  margin-top: 12px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-appearance-contribution .kg-settings-compact-section[data-ds-component="ds/sub-panel"] {
  padding: 14px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-appearance-contribution .kg-settings-compact-section > .ds-sub-panel__body {
  gap: 10px;
}

/* Ultimate visual-fidelity pass. These refinements stay inside the governed
 * staging Settings profile and do not style the shared Tutor Desk shell. */
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__illustration-icon {
  display: block;
  overflow: visible;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__header-icon .tutor-desk-settings-v2__illustration-icon--palette {
  width: 25px;
  height: 25px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__header-icon :where(
  .tutor-desk-settings-v2__illustration-icon--notification,
  .tutor-desk-settings-v2__illustration-icon--users
) {
  width: 25px;
  height: 25px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] [data-settings-reference-v2-segmented="defaultLessonTypeId"] .tutor-desk-settings-v2__segment {
  min-height: 112px;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  padding: 10px 14px;
  text-align: center;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] [data-settings-reference-v2-segmented="defaultLessonTypeId"] .tutor-desk-settings-v2__segment-icon,
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] [data-settings-reference-v2-segmented="defaultLessonTypeId"] .tutor-desk-settings-v2__segment.is-selected .tutor-desk-settings-v2__segment-icon {
  width: 82px;
  height: 62px;
  flex: 0 0 62px;
  border-radius: 0;
  background: transparent;
  color: var(--tds-v2-action);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] [data-settings-reference-v2-segmented="defaultLessonTypeId"] .tutor-desk-settings-v2__segment-icon svg {
  width: 82px;
  height: 62px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] [data-settings-reference-v2-segmented="brandingMode"] .tutor-desk-settings-v2__segment {
  min-height: 100px;
  gap: 13px;
  padding: 12px 16px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] [data-settings-reference-v2-segmented="brandingMode"] .tutor-desk-settings-v2__segment-icon,
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] [data-settings-reference-v2-segmented="brandingMode"] .tutor-desk-settings-v2__segment.is-selected .tutor-desk-settings-v2__segment-icon {
  width: 58px;
  height: 54px;
  flex: 0 0 58px;
  border-radius: 0;
  background: transparent;
  color: var(--tds-v2-action);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] [data-settings-reference-v2-segmented="brandingMode"] .tutor-desk-settings-v2__segment-icon svg {
  width: 58px;
  height: 54px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-final-theme-preview[data-theme-mode="system"] {
  background: linear-gradient(90deg, #f5f8ff 0 50%, #07111f 50% 100%);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-final-theme-preview[data-theme-mode="system"] :where(
  .kg-settings-final-theme-preview__rail,
  .kg-settings-final-theme-preview__canvas,
  .kg-settings-final-theme-preview__bar,
  .kg-settings-final-theme-preview__card,
  .kg-settings-final-theme-preview__rail-line
) {
  background: linear-gradient(90deg, #e6edf8 0 50%, #13233a 50% 100%);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-final-theme-preview[data-theme-mode="system"] .kg-settings-final-theme-preview__rail-line--active {
  background: var(--tds-v2-action);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-appearance-contribution > .ds-sub-panel__body > .kg-settings-contribution-host {
  width: calc(100% + 36px);
  margin-inline: -18px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-appearance-panel--previews > .ds-sub-panel__header {
  padding: 16px 16px 0;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-appearance-panel--previews > .ds-sub-panel__body {
  gap: 12px;
  padding: 10px 16px 14px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-appearance-contribution .tutor-desk-settings-v2__section-copy {
  gap: 3px;
  padding-bottom: 10px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-appearance-contribution .tutor-desk-settings-v2__scope {
  margin-top: 2px;
  padding: 3px 7px;
  font-size: 10px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] [data-settings-v2-subjects-state="empty"] {
  min-height: 0;
  padding: 0;
  border: 0;
  background: transparent;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] [data-settings-v2-subjects-state="empty"] > .kg-settings-notice {
  width: 100%;
  min-height: 48px;
  align-content: center;
  padding-block: 8px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.branding"] [data-settings-v2-resource="tenant:branding"] .tutor-desk-settings-v2__form {
  gap: 12px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.branding"] [data-settings-v2-resource="tenant:branding"] .tutor-desk-settings-v2__section-copy {
  padding-bottom: 10px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.branding"] [data-settings-v2-resource="tenant:branding"] .tutor-desk-settings-v2__section-copy > p:not(.tutor-desk-settings-v2__scope),
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.branding"] [data-settings-v2-resource="tenant:branding"] .tutor-desk-settings-v2__scope {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  white-space: nowrap;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.branding"] [data-settings-reference-v2-group="branding-mode"],
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.branding"] [data-settings-reference-v2-group="brand-messages"] {
  padding: 10px 12px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.branding"] [data-settings-reference-v2-group="brand-messages"] .tutor-desk-settings-v2__visual-group-header {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  white-space: nowrap;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.branding"] [data-settings-v2-field="portalWelcomeMessage"] :where(.ds-textarea__control, .ds-textarea__native) {
  min-height: 48px;
  height: 48px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__branding-workflow > .ds-sub-panel__body {
  gap: 14px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__branding-status > .tutor-desk-settings-v2__grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__branding-status .tutor-desk-settings-v2__field {
  min-width: 190px;
  min-height: 36px;
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 7px 10px;
  border: 1px solid var(--tds-v2-border);
  border-radius: 8px;
  background: var(--tds-v2-surface-soft);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__branding-status .tutor-desk-settings-v2__field::before {
  content: "";
  width: 8px;
  height: 8px;
  flex: 0 0 8px;
  border-radius: 50%;
  background: var(--tds-v2-action);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__branding-status .tutor-desk-settings-v2__field:nth-child(2)::before {
  background: var(--tds-v2-success);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__branding-status .tutor-desk-settings-v2__fine-print {
  font-size: 12px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__branding-studio {
  min-width: 0;
  display: grid;
  grid-template-columns: minmax(250px, .78fr) minmax(0, 2fr);
  gap: 18px;
  align-items: stretch;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] :where(
  .tutor-desk-settings-v2__branding-upload-form,
  .tutor-desk-settings-v2__branding-palette-form
) {
  align-content: start;
  gap: 10px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__branding-palette-form {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__branding-colour-card {
  min-width: 0;
  display: grid;
  grid-template-rows: 92px auto;
  gap: 8px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__branding-colour-swatch {
  display: block;
  border: 1px solid color-mix(in srgb, var(--tds-v2-text) 12%, transparent);
  border-radius: 9px;
  background: var(--tds-v2-surface-soft);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, .22);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__branding-colour-card:has(option[value="#0F766E"]:checked) .tutor-desk-settings-v2__branding-colour-swatch { background: linear-gradient(135deg, #0f766e, #0b5f59); }
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__branding-colour-card:has(option[value="#1D4ED8"]:checked) .tutor-desk-settings-v2__branding-colour-swatch,
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__branding-colour-card:has(option[value="#1E3A8A"]:checked) .tutor-desk-settings-v2__branding-colour-swatch { background: linear-gradient(135deg, #2563eb, #1e3a8a); }
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__branding-colour-card:has(option[value="#4338CA"]:checked) .tutor-desk-settings-v2__branding-colour-swatch { background: linear-gradient(135deg, #4f46e5, #3730a3); }
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__branding-colour-card:has(option[value="#6D28D9"]:checked) .tutor-desk-settings-v2__branding-colour-swatch { background: linear-gradient(135deg, #7c3aed, #5b21b6); }
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__branding-colour-card:has(option[value="#9F1239"]:checked) .tutor-desk-settings-v2__branding-colour-swatch { background: linear-gradient(135deg, #be123c, #881337); }
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__branding-colour-card:has(option[value="#374151"]:checked) .tutor-desk-settings-v2__branding-colour-swatch { background: linear-gradient(135deg, #4b5563, #1f2937); }
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__branding-colour-card:has(option[value="#F59E0B"]:checked) .tutor-desk-settings-v2__branding-colour-swatch { background: linear-gradient(135deg, #f59e0b, #f97316); }
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__branding-colour-card:has(option[value="#22C55E"]:checked) .tutor-desk-settings-v2__branding-colour-swatch { background: linear-gradient(135deg, #22c55e, #16a34a); }
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__branding-colour-card:has(option[value="#38BDF8"]:checked) .tutor-desk-settings-v2__branding-colour-swatch { background: linear-gradient(135deg, #38bdf8, #0284c7); }
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__branding-colour-card:has(option[value="#F472B6"]:checked) .tutor-desk-settings-v2__branding-colour-swatch { background: linear-gradient(135deg, #f472b6, #db2777); }
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__branding-colour-card:has(option[value="#A78BFA"]:checked) .tutor-desk-settings-v2__branding-colour-swatch { background: linear-gradient(135deg, #a78bfa, #7c3aed); }
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__branding-colour-card:has(option[value="#E5E7EB"]:checked) .tutor-desk-settings-v2__branding-colour-swatch,
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__branding-colour-card:has(option[value="#FDE68A"]:checked) .tutor-desk-settings-v2__branding-colour-swatch { background: linear-gradient(135deg, #fde68a, #e5e7eb); }

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__branding-upload-form .tutor-desk-settings-v2__upload-surface {
  min-height: 142px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] :where(
  .tutor-desk-settings-v2__branding-upload-form,
  .tutor-desk-settings-v2__branding-palette-form
) > .tutor-desk-settings-v2__actions {
  grid-column: 1 / -1;
}

/* Final symmetry and descriptive-preview correction. This is presentation-only:
 * every existing field, form, save boundary, and catalog disclosure stays in
 * place while paired cards use deliberate geometry at desktop widths. */
@media (min-width: 1024px) {
  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .ds-plugin-page-panel__content-region,
  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-shell-body,
  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-layout {
    height: 100%;
    min-height: 0;
    overflow: hidden;
  }

  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-nav {
    position: static;
    height: 100%;
  }

  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-content {
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
  }
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] :where(
  .kg-settings-appearance-contribution .tutor-desk-settings-v2,
  .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.profile-business"],
  .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.schedule-defaults"],
  .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.students-portal"],
  .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.payments-invoicing"]
) {
  align-items: stretch;
}

/* The three real theme choices keep their existing actions and persistence,
 * but use the clearer descriptive illustrations from the approved reference. */
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-final-theme-card {
  grid-template-rows: 120px auto;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-final-theme-preview {
  --tds-v2-preview-canvas: #f8fafc;
  --tds-v2-preview-rail: #eef3fa;
  --tds-v2-preview-surface: #ffffff;
  --tds-v2-preview-border: #d4deeb;
  --tds-v2-preview-line: #c5d0df;
  --tds-v2-preview-accent: #287cff;
  height: 120px;
  min-height: 120px;
  max-height: 120px;
  grid-template-columns: 72px minmax(0, 1fr);
  border-color: var(--tds-v2-preview-border);
  background: var(--tds-v2-preview-canvas);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, .34);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-final-theme-preview[data-theme-mode="dark"] {
  --tds-v2-preview-canvas: #07111f;
  --tds-v2-preview-rail: #0b1c31;
  --tds-v2-preview-surface: #12233a;
  --tds-v2-preview-border: #24415f;
  --tds-v2-preview-line: #315170;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-final-theme-preview__rail,
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-final-theme-preview__canvas {
  gap: 8px;
  padding: 10px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-final-theme-preview__rail {
  border-right-color: var(--tds-v2-preview-border);
  background: var(--tds-v2-preview-rail);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-final-theme-preview__canvas {
  background: var(--tds-v2-preview-canvas);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-final-theme-preview__wordmark {
  width: 100%;
  height: 36px;
  min-height: 36px;
  display: grid;
  place-items: center;
  overflow: visible;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-final-theme-preview__wordmark img {
  display: none;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-final-theme-preview__wordmark::before {
  content: "T";
  color: var(--tds-v2-preview-accent);
  font-family: Georgia, "Times New Roman", serif;
  font-size: 30px;
  font-weight: 800;
  line-height: 1;
  text-shadow: 0 1px 1px rgba(15, 31, 59, .24);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-final-theme-preview__rail-line,
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-final-theme-preview__bar,
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-final-theme-preview__card {
  border-color: var(--tds-v2-preview-border);
  background: var(--tds-v2-preview-line);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-final-theme-preview__rail-line {
  height: 6px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-final-theme-preview__rail-line--active {
  background: var(--tds-v2-preview-accent);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-final-theme-preview__bar {
  height: 18px;
  border: 1px solid var(--tds-v2-preview-border);
  background: var(--tds-v2-preview-surface);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-final-theme-preview__cards {
  min-height: 62px;
  gap: 8px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-final-theme-preview__card {
  min-height: 62px;
  background: var(--tds-v2-preview-surface);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-final-theme-preview[data-theme-mode="system"] {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  background: linear-gradient(90deg, #eef4fd 0 50%, #0b1b31 50% 100%);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-final-theme-preview[data-theme-mode="system"] :where(
  .kg-settings-final-theme-preview__wordmark,
  .kg-settings-final-theme-preview__rail-line,
  .kg-settings-final-theme-preview__bar,
  .kg-settings-final-theme-preview__cards
) {
  display: none;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-final-theme-preview[data-theme-mode="system"] :where(
  .kg-settings-final-theme-preview__rail,
  .kg-settings-final-theme-preview__canvas
) {
  min-width: 0;
  display: grid;
  grid-template-rows: 46px 30px;
  align-content: center;
  justify-items: center;
  gap: 10px;
  padding: 12px;
  border: 0;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-final-theme-preview[data-theme-mode="system"] .kg-settings-final-theme-preview__rail {
  border-right: 1px solid #4b6b91;
  background: rgba(238, 244, 253, .92);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-final-theme-preview[data-theme-mode="system"] .kg-settings-final-theme-preview__canvas {
  background: rgba(11, 27, 49, .92);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-final-theme-preview[data-theme-mode="system"] :where(
  .kg-settings-final-theme-preview__rail,
  .kg-settings-final-theme-preview__canvas
)::before {
  content: "";
  width: 42px;
  height: 42px;
  align-self: center;
  background: #4c8dff;
  mask-position: center;
  mask-repeat: no-repeat;
  mask-size: contain;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-final-theme-preview[data-theme-mode="system"] .kg-settings-final-theme-preview__rail::before {
  mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Ccircle cx='12' cy='12' r='4' fill='black'/%3E%3Cpath d='M12 1v3M12 20v3M1 12h3M20 12h3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M19.8 4.2l-2.1 2.1M6.3 17.7l-2.1 2.1' fill='none' stroke='black' stroke-width='2' stroke-linecap='round'/%3E%3C/svg%3E");
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-final-theme-preview[data-theme-mode="system"] .kg-settings-final-theme-preview__canvas::before {
  mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M20.5 15.5A9 9 0 0 1 8.5 3.5 9 9 0 1 0 20.5 15.5Z' fill='black'/%3E%3C/svg%3E");
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-final-theme-preview[data-theme-mode="system"] :where(
  .kg-settings-final-theme-preview__rail,
  .kg-settings-final-theme-preview__canvas
)::after {
  content: "";
  width: min(76px, 78%);
  height: 28px;
  background:
    linear-gradient(#5a83ba, #5a83ba) center 0 / 100% 6px no-repeat,
    linear-gradient(#5a83ba, #5a83ba) center 11px / 78% 6px no-repeat,
    linear-gradient(#5a83ba, #5a83ba) center 22px / 54% 6px no-repeat;
  opacity: .9;
}

/* Deliberate peer-card geometry. These cards retain independent forms and save
 * scopes; only their rendered surfaces and existing actions share a baseline. */
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-appearance-contribution .tutor-desk-settings-v2 > [data-settings-v2-resource],
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.profile-business"] > [data-settings-v2-resource="personal:profile"],
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.profile-business"] > [data-settings-v2-resource="tenant:billing"] {
  height: 100%;
  align-self: stretch;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-appearance-contribution .tutor-desk-settings-v2 > [data-settings-v2-resource] > .ds-sub-panel__body,
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.profile-business"] > :where(
  [data-settings-v2-resource="personal:profile"],
  [data-settings-v2-resource="tenant:billing"]
) > .ds-sub-panel__body,
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-appearance-contribution .tutor-desk-settings-v2 > [data-settings-v2-resource] .tutor-desk-settings-v2__form,
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.profile-business"] > :where(
  [data-settings-v2-resource="personal:profile"],
  [data-settings-v2-resource="tenant:billing"]
) .tutor-desk-settings-v2__form {
  min-height: 100%;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-appearance-contribution .tutor-desk-settings-v2 > [data-settings-v2-resource] .tutor-desk-settings-v2__form > .tutor-desk-settings-v2__actions,
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.profile-business"] > :where(
  [data-settings-v2-resource="personal:profile"],
  [data-settings-v2-resource="tenant:billing"]
) .tutor-desk-settings-v2__form > .tutor-desk-settings-v2__actions {
  align-self: end;
  margin-top: auto;
}

/* The existing Calendar hand-off is the right-hand peer of Lesson defaults.
 * Stretching and centring its real illustration, copy, and action removes the
 * false dead zone without moving Calendar behavior into Settings. */
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.schedule-defaults"] > [data-settings-reference-v2-card="availability"] {
  height: 100%;
  align-self: stretch;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.schedule-defaults"] > [data-settings-reference-v2-card="availability"] > .ds-sub-panel__body {
  min-height: 100%;
  align-content: center;
  gap: 18px;
}

/* Quiet Hours is one real compound setting. Its existing Enable, Starts, and
 * Ends controls use the whole card as one balanced desktop row. */
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.notifications"] [data-settings-reference-v2-group="quiet-hours"] .tutor-desk-settings-v2__visual-group-grid > [data-settings-v2-field="quietHours"] {
  grid-column: 1 / -1;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.notifications"] [data-settings-v2-field="quietHours"] .tutor-desk-settings-v2__fieldset > .tutor-desk-settings-v2__grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
  align-items: start;
  gap: 16px;
}

/* Real version-history previews need a stable surface even when an image is
 * transparent or mostly whitespace. The image endpoint and actions are
 * unchanged; this only gives each existing version a compact visual slot. */
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__branding-version {
  grid-template-columns: minmax(180px, 1fr) minmax(240px, .9fr) minmax(180px, auto);
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__branding-version > figure {
  min-width: 0;
  display: grid;
  grid-template-columns: 112px minmax(0, 1fr);
  align-items: center;
  gap: 12px;
  margin: 0;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__branding-version .tutor-desk-settings-v2__logo-preview {
  width: 112px;
  height: 64px;
  max-width: none;
  max-height: none;
  padding: 7px;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__branding-version figcaption {
  min-width: 0;
  color: var(--tds-v2-muted);
  font-size: 12px;
  line-height: 1.45;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__branding-version > .tutor-desk-settings-v2__actions {
  justify-self: end;
}

/* Paired billing cards share row height, and the existing independent payment
 * method and notification save scopes occupy one aligned bottom row. */
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.payments-invoicing"] [data-settings-v2-resource="tenant:billing"] .tutor-desk-settings-v2__grid {
  align-items: stretch;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.payments-invoicing"] [data-settings-v2-resource="tenant:billing"] .tutor-desk-settings-v2__visual-group {
  height: 100%;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.payments-invoicing"] > [data-settings-v2-catalog="payment_method"] {
  order: 1;
  align-self: stretch;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.payments-invoicing"] > [data-settings-v2-resource="tenant:notifications"] {
  order: 2;
  align-self: stretch;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.payments-invoicing"] > :where(
  [data-settings-v2-catalog="payment_method"],
  [data-settings-v2-resource="tenant:notifications"]
) > .ds-sub-panel__body {
  flex: 1 1 auto;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.payments-invoicing"] > [data-settings-v2-resource="tenant:notifications"] .tutor-desk-settings-v2__form {
  min-height: 100%;
  grid-template-rows: auto auto minmax(0, 1fr) auto;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.payments-invoicing"] > [data-settings-v2-resource="tenant:notifications"] .tutor-desk-settings-v2__actions {
  align-self: end;
}

/* This projection contains one real toggle. Let it use the complete invoice
 * notification card instead of inheriting the two-column group matrix and
 * leaving an unexplained empty half beside the control. */
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.payments-invoicing"] > [data-settings-v2-resource="tenant:notifications"] [data-settings-reference-v2-group="invoice-notifications"] {
  grid-column: 1 / -1;
}

/* The real editable catalogs cannot use the reference image's static Grade
 * summary. Two compact peer cards followed by one full-width multi-column Grade
 * card keeps every governed disclosure while eliminating the tall centre strip. */
html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.students-portal"] > [data-settings-v2-catalog="student_status"] {
  grid-column: 1 / span 6;
  order: 1;
  align-self: stretch;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.students-portal"] > [data-settings-v2-catalog="lesson_type"] {
  grid-column: 7 / -1;
  order: 2;
  align-self: stretch;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.students-portal"] > [data-settings-v2-catalog="grade"] {
  grid-column: 1 / -1;
  order: 3;
}

html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.students-portal"] > [data-settings-v2-catalog="grade"] .tutor-desk-settings-v2__catalog-list {
  grid-template-columns: repeat(3, minmax(0, 1fr));
  align-items: start;
}

@media (max-width: 1279px) {
  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.students-portal"] > [data-settings-v2-catalog="grade"] .tutor-desk-settings-v2__catalog-list {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

/* Tablet and mobile Settings navigation sheet. */
@media (max-width: 1439px) and (min-width: 1024px) {
  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-layout {
    grid-template-columns: 272px minmax(0, 1fr);
  }

  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-nav {
    width: 272px;
  }

  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-content {
    padding-inline: 30px;
  }
}

@media (max-width: 1023px) {
  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-layout {
    display: block;
    min-height: 0;
  }

  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-content {
    padding: 24px 22px 52px;
  }

  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] :where(
    .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.students-portal"],
    .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.payments-invoicing"]
  ) > :where([data-settings-v2-catalog], [data-settings-v2-resource="tenant:notifications"]) {
    grid-column: 1 / -1;
    order: initial;
  }

  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-nav-toggle {
    width: calc(100% - 44px);
    min-height: 48px;
    display: flex;
    align-items: center;
    gap: 11px;
    margin: 20px 22px 0;
    padding: 10px 14px;
    border: 1px solid var(--tds-v2-border);
    border-radius: 10px;
    background: var(--tds-v2-surface);
    color: var(--tds-v2-text);
    font: inherit;
    text-align: left;
  }

  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-nav-toggle > span {
    width: 20px;
    height: 14px;
    border-top: 2px solid currentColor;
    border-bottom: 2px solid currentColor;
    box-shadow: inset 0 5px transparent, inset 0 7px currentColor;
  }

  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-nav-backdrop:not([hidden]) {
    position: fixed;
    z-index: 880;
    inset: 0;
    display: block;
    border: 0;
    background: rgba(7, 17, 31, 0.56);
    backdrop-filter: blur(2px);
  }

  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-nav {
    position: fixed;
    z-index: 900;
    top: 0;
    bottom: 0;
    left: 0;
    width: min(340px, calc(100vw - 44px));
    height: 100dvh;
    border-right: 1px solid var(--tds-v2-border);
    box-shadow: 18px 0 55px rgba(7, 17, 31, 0.24);
    transform: translateX(-105%);
    visibility: hidden;
    transition: transform 180ms ease, visibility 0s linear 180ms;
  }

  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-nav[data-open="true"] {
    transform: translateX(0);
    visibility: visible;
    transition: transform 180ms ease, visibility 0s linear 0s;
  }

  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-nav__header {
    grid-template-columns: minmax(0, 1fr) 44px;
    align-items: start;
  }

  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-nav__close {
    width: 44px;
    height: 44px;
    display: grid;
    place-items: center;
    border: 1px solid var(--tds-v2-border);
    border-radius: 9px;
    background: var(--tds-v2-surface-soft);
    color: var(--tds-v2-text);
  }

  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-nav__close > span {
    position: relative;
    width: 18px;
    height: 18px;
  }

  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-nav__close > span::before,
  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-nav__close > span::after {
    content: "";
    position: absolute;
    top: 8px;
    left: 1px;
    width: 16px;
    height: 2px;
    border-radius: 2px;
    background: currentColor;
  }

  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-nav__close > span::before { transform: rotate(45deg); }
  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-nav__close > span::after { transform: rotate(-45deg); }

  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-pack-grid--appearance {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.students-portal"] > [data-settings-v2-resource="tenant:schedule"] .tutor-desk-settings-v2__form {
    grid-template-columns: minmax(0, 1fr) auto;
  }

  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.students-portal"] > [data-settings-v2-resource="tenant:schedule"] .tutor-desk-settings-v2__form > .tutor-desk-settings-v2__section-copy {
    grid-column: 1 / -1;
  }

  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.students-portal"] > [data-settings-v2-resource="tenant:schedule"] .tutor-desk-settings-v2__form > .tutor-desk-settings-v2__grid {
    grid-column: 1;
  }

  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.students-portal"] > [data-settings-v2-resource="tenant:schedule"] .tutor-desk-settings-v2__form > .tutor-desk-settings-v2__actions {
    grid-column: 2;
  }
}

@media (max-width: 767px) {
  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-content {
    padding: 22px 16px 44px;
  }

  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-nav-toggle {
    width: calc(100% - 32px);
    margin-inline: 16px;
  }

  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.students-portal"] > [data-settings-v2-catalog="grade"] .tutor-desk-settings-v2__catalog-list {
    grid-template-columns: minmax(0, 1fr);
  }

  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.notifications"] [data-settings-v2-field="quietHours"] .tutor-desk-settings-v2__fieldset > .tutor-desk-settings-v2__grid,
  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__branding-version > figure {
    grid-template-columns: minmax(0, 1fr);
  }

  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] :where(
    .tutor-desk-settings-v2,
    .tutor-desk-settings-v2__form,
    .tutor-desk-settings-v2__grid,
    .tutor-desk-settings-v2__choice-grid,
    .kg-settings-pack-grid--appearance,
    .tutor-desk-settings-v2__branding-studio,
    .tutor-desk-settings-v2__branding-palette-form
  ) {
    grid-template-columns: minmax(0, 1fr);
  }

  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-compact-section[data-ds-component="ds/sub-panel"] {
    padding: 18px;
  }

  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] :where(
    .kg-settings-appearance-contribution .tutor-desk-settings-v2,
    .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.profile-business"],
    .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.notifications"] [data-settings-v2-resource] .tutor-desk-settings-v2__grid,
    .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.payments-invoicing"] [data-settings-v2-resource] .tutor-desk-settings-v2__grid,
    .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.ai-automation"] [data-settings-v2-resource="tenant:ai"] .tutor-desk-settings-v2__grid,
    .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.students-portal"] > [data-settings-v2-resource="tenant:schedule"] .tutor-desk-settings-v2__form
  ) {
    grid-template-columns: minmax(0, 1fr);
  }

  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.profile-business"] > [data-settings-v2-resource],
  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.ai-automation"] .tutor-desk-settings-v2__ai-group,
  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.students-portal"] > [data-settings-v2-resource="tenant:schedule"] .tutor-desk-settings-v2__form > :where(.tutor-desk-settings-v2__section-copy, .tutor-desk-settings-v2__grid, .tutor-desk-settings-v2__actions) {
    grid-column: 1 / -1;
    grid-row: auto;
  }

  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.ai-automation"] .tutor-desk-settings-v2__ai-group {
    grid-row: auto;
  }

  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] :where(
    .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.workspace-views"],
    .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.notifications"],
    .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.branding"],
    .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.ai-automation"]
  ) [data-settings-v2-resource] .tutor-desk-settings-v2__form > .tutor-desk-settings-v2__actions {
    position: static;
  }

  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.schedule-defaults"] > [data-settings-v2-resource="tenant:schedule"] .tutor-desk-settings-v2__section-copy {
    padding-right: 0;
  }

  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.schedule-defaults"] > [data-settings-v2-resource="tenant:schedule"] .tutor-desk-settings-v2__form > .tutor-desk-settings-v2__actions {
    position: static;
  }

  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] [data-settings-reference-v2-segmented="defaultLessonDurationMinutes"] .tutor-desk-settings-v2__segments {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__branding-version {
    grid-template-columns: minmax(0, 1fr);
  }

  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.notifications"] .tutor-desk-settings-v2__section-copy {
    padding-right: 0;
  }

  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.notifications"] .tutor-desk-settings-v2__form > .tutor-desk-settings-v2__actions {
    position: static;
    padding-top: 2px;
  }

  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.payments-invoicing"] [data-settings-v2-resource="tenant:billing"] .tutor-desk-settings-v2__form > .tutor-desk-settings-v2__actions {
    position: static;
    padding-top: 2px;
  }

  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2[data-settings-v2-descriptor="tutor-desk.payments-invoicing"] [data-settings-reference-v2-group="payment-default"] {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media (max-width: 479px) {
  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-content {
    padding-inline: 12px;
  }

  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .kg-settings-reference-v2-nav-toggle {
    width: calc(100% - 24px);
    margin-inline: 12px;
  }

  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__actions {
    align-items: stretch;
    flex-direction: column;
  }

  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__actions::before {
    display: none;
  }

  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__actions .ds-button {
    width: 100%;
  }

  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .tutor-desk-settings-v2__segments {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media (prefers-reduced-motion: reduce) {
  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] *,
  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] *::before,
  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] *::after {
    scroll-behavior: auto;
    animation-duration: 0.01ms;
    animation-iteration-count: 1;
    transition-duration: 0.01ms;
  }
}

@media (forced-colors: active) {
  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] {
    --tds-v2-canvas: Canvas;
    --tds-v2-surface: Canvas;
    --tds-v2-surface-soft: Canvas;
    --tds-v2-surface-hover: Highlight;
    --tds-v2-border: CanvasText;
    --tds-v2-border-strong: CanvasText;
    --tds-v2-text: CanvasText;
    --tds-v2-muted: CanvasText;
    --tds-v2-action: Highlight;
    --tds-v2-action-hover: Highlight;
    --tds-v2-action-soft: Canvas;
    --tds-v2-action-contrast: HighlightText;
    --tds-v2-danger: LinkText;
    forced-color-adjust: auto;
  }

  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] :where(
    .kg-settings-reference-v2-nav__icon,
    .tutor-desk-settings-v2__upload-icon
  ) {
    background: ButtonText;
  }

  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] :where(
    .kg-settings-reference-v2-nav__item.is-active,
    .tutor-desk-settings-v2__segment.is-selected,
    .kg-settings-reference-v2-select__option.is-selected
  ) {
    border-color: Highlight;
    background: Highlight;
    color: HighlightText;
  }

  html[data-kg-ui-standard="ttoria-saas-final-ui-v1"][data-kg-runtime-target="saas-staging"][data-kg-settings-ui-profile="reference-v2"] .kg-settings-root[data-kg-settings-reference-v2="true"] .ds-checkbox__mark {
    border: 1px solid CanvasText;
  }
}
`;
